// SWR fetch + post-processing for the theming tab responses table.
// Applies client-side "unthemed OR selected themes" filter the API can't express.
// comprehendProcessing is injected by the parent (which owns the polling lifecycle
// and ties it to toast/loading bar state). URL builder is co-located as the SWR key factory.

import { useEffect, useState } from "react";
import useSWRInfinite from "swr/infinite";
import { fetcherSimple } from "utils/api";
import {
    COMPREHEND_POLL_INTERVAL_MS,
    RESPONSES_PAGE_SIZE,
} from "../theming-tab.config";

// Stable identity default so the counter-propagation useEffect doesn't
// re-fire every render when callers omit onCounterUpdate.
const NOOP = () => {};

function joinParam(values, key) {
    if (!values?.length) return "";
    return values
        .map((v) => encodeURIComponent(v))
        .join(`&${key}=`);
}

function buildCustomAnswersUrl(surveyId, filters, textSearch, pageIndex) {
    const questionParams = joinParam(
        filters?.questions?.map((q) => q.nanoid),
        "question",
    );
    // Unthemed: skip the theme= filter so the API returns everything;
    // OR-logic is applied client-side in the hook.
    const themeParams = filters?.unthemed
        ? ""
        : joinParam(
            filters?.themes?.map((t) => t.id),
            "theme",
        );
    const textParams = joinParam(
        filters?.texts?.map((t) => t.text),
        "text",
    );
    const keywordParams = joinParam(
        filters?.keywords?.map((k) => k.text),
        "text",
    );
    const liveSearchParam = textSearch ? encodeURIComponent(textSearch) : "";

    const qs = [
        questionParams ? `&question=${questionParams}` : "",
        themeParams ? `&theme=${themeParams}` : "",
        textParams ? `&text=${textParams}` : "",
        keywordParams ? `&text=${keywordParams}` : "",
        liveSearchParam ? `&text=${liveSearchParam}` : "",
    ].join("");

    return `/v2/survey/${surveyId}/custom_answers/filter?page=${
        pageIndex + 1
    }&rows=${RESPONSES_PAGE_SIZE}${qs}`;
}

export function useResponsesData({
    surveyId,
    filters,
    textSearch,
    comprehendProcessing,
    onCounterUpdate = NOOP,
}) {
    const getKey = (pageIndex, previousPageData) => {
        if (previousPageData && !previousPageData?.data.length) return null;
        return buildCustomAnswersUrl(
            surveyId,
            filters,
            textSearch,
            pageIndex,
        );
    };
    const fetcher = (path) => fetcherSimple(path, "GET");

    const {
        data: customResponsesFilter,
        size: customResponsesFilterSize,
        setSize: customResponsesFilterSetSize,
        isLoading: customResponsesFilterLoading,
        mutate: customResponsesFilterMutate,
    } = useSWRInfinite(getKey, fetcher, {
        refreshInterval: comprehendProcessing ? COMPREHEND_POLL_INTERVAL_MS : 0,
    });

    const customResponsesFilterPager = customResponsesFilter?.[0]?.pager;

    const [customResponsesFilterData, setCustomResponsesFilterData] = useState(
        [],
    );
    const [counter, setCounter] = useState(null);
    const [themeCounts, setThemeCounts] = useState({});
    const [questionCounts, setQuestionCounts] = useState({});
    const [allResponses, setAllResponses] = useState([]);

    // Flatten pages into a single array.
    useEffect(() => {
        if (customResponsesFilter?.length > 0) {
            const dataFromSet = customResponsesFilter.map(
                (dataSet) => dataSet?.data,
            );
            setCustomResponsesFilterData([].concat(...dataFromSet));
        }
    }, [customResponsesFilter]);

    // Extract counter data + propagate to parent for cross-tab use.
    useEffect(() => {
        if (customResponsesFilter?.[0]?.counter) {
            const newCounter = customResponsesFilter[0].counter;
            setCounter(newCounter);
            onCounterUpdate(newCounter);
        }
    }, [customResponsesFilter, onCounterUpdate]);

    // Derive per-theme and per-question counts from the counter object.
    useEffect(() => {
        if (counter?.theme_counts) setThemeCounts(counter.theme_counts);
        if (counter?.question_counts) setQuestionCounts(counter.question_counts);
    }, [counter]);

    // Apply the "unthemed OR selected themes" client-side filter that the
    // API cannot express.
    useEffect(() => {
        let filteredResponses = customResponsesFilterData;
        if (filters.unthemed) {
            if (filters.themes && filters.themes.length > 0) {
                const themeIds = filters.themes.map((t) => t.id);
                filteredResponses = customResponsesFilterData?.filter(
                    (response) =>
                        response.theme_count === 0 ||
                        Object.keys(response.themes || {}).some((themeId) =>
                            themeIds.includes(Number(themeId)),
                        ),
                );
            } else {
                filteredResponses = customResponsesFilterData?.filter(
                    (response) => response.theme_count === 0,
                );
            }
        }
        setAllResponses(filteredResponses);
    }, [customResponsesFilterData, filters.unthemed, filters.themes]);

    const isEmpty = customResponsesFilter?.[0]?.data?.length === 0;
    const lastIdx = customResponsesFilter ? customResponsesFilter.length - 1 : 0;
    const customResponsesFilterIsReachingEnd =
        isEmpty ||
        (customResponsesFilter &&
            customResponsesFilter[lastIdx]?.data?.length <
                RESPONSES_PAGE_SIZE);

    return {
        customResponsesFilter,
        customResponsesFilterPager,
        customResponsesFilterSize,
        customResponsesFilterSetSize,
        customResponsesFilterLoading,
        customResponsesFilterMutate,
        customResponsesFilterIsReachingEnd,
        counter,
        themeCounts,
        questionCounts,
        allResponses,
    };
}
